import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClockRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { StatusChipV3 } from '@labrav/react-components'
import { FundingTemplateUsageEntry } from '../../api/fundingApi'
import { formatWalletDate } from '../../utils/format'
import { statusChip } from '../../utils/statusChip'
import { useStyles } from './TemplateUsagesModal.styles'

type Props = {
  open: boolean
  onClose: () => void
  entries: FundingTemplateUsageEntry[]
  loading?: boolean
  /** Navigate to the funding request detail when the request link is clicked. */
  onOpenRequest?: (entry: FundingTemplateUsageEntry) => void
}

const companyInitial = (name: string) => {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toUpperCase()
}

const createdByLine = (
  createdBy: string | null | undefined,
  usedAt: string | null | undefined
) => {
  const date = formatWalletDate(usedAt)
  if (createdBy?.trim() && date) return `${createdBy.trim()} on ${date}`
  if (createdBy?.trim()) return createdBy.trim()
  return date ?? '—'
}

export const TemplateUsagesModal = ({
  open,
  onClose,
  entries,
  loading = false,
  onOpenRequest,
}: Props) => {
  const { classes, cx } = useStyles()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      data-testid="template-usages-modal"
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.titleGroup}>
            <span className={classes.titleIcon}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography component="h2" className={classes.title}>
              Template used in the following funding requests
            </Typography>
          </div>
        </div>
        <button
          type="button"
          className={classes.close}
          aria-label="Close"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className={classes.table}>
        <div className={classes.headerRow}>
          <div className={cx(classes.headerCell, classes.colCompany)}>
            Company name
          </div>
          <div className={cx(classes.headerCell, classes.colRequest)}>
            Funding request ID
          </div>
          <div className={cx(classes.headerCell, classes.colCreatedBy)}>
            Created by
          </div>
          <div className={cx(classes.headerCell, classes.colStatus)}>
            Status
          </div>
        </div>
        {loading ? (
          <Typography component="p" className={classes.empty}>
            Loading…
          </Typography>
        ) : entries.length === 0 ? (
          <Typography component="p" className={classes.empty}>
            This template has not been used in any funding requests yet.
          </Typography>
        ) : (
          entries.map((entry, index) => {
            const company = entry.company_name?.trim() || ''
            const requestTitle =
              entry.funding_request_title?.trim() ||
              entry.opportunity_id?.trim() ||
              entry.program_label?.trim() ||
              entry.workflow_id
            const statusKey = (entry.status || '')
              .trim()
              .toUpperCase()
              .replace(/\s+/g, '_')
            const chip = statusKey ? statusChip(statusKey) : null
            return (
              <div
                key={entry.workflow_id}
                className={cx(
                  classes.row,
                  index % 2 === 0 ? classes.rowAlt : classes.rowPlain
                )}
                data-testid="template-usage-row"
              >
                <div className={classes.cellCompany}>
                  {company ? (
                    <span className={classes.companyCell}>
                      <span
                        className={classes.companyAvatar}
                        aria-hidden
                      >
                        {companyInitial(company)}
                      </span>
                      <span className={classes.companyName}>{company}</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </div>
                <div className={classes.cellRequest}>
                  {onOpenRequest ? (
                    <button
                      type="button"
                      className={classes.requestLink}
                      onClick={() => onOpenRequest(entry)}
                    >
                      {requestTitle}
                    </button>
                  ) : (
                    requestTitle
                  )}
                </div>
                <div className={classes.cellCreatedBy}>
                  {createdByLine(entry.created_by, entry.used_at)}
                </div>
                <div className={classes.cellStatus}>
                  {chip ? (
                    <StatusChipV3
                      bgColor={chip.bg}
                      textColor={chip.text}
                      label={chip.label}
                      sizeVarient="normal"
                    />
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </Dialog>
  )
}

export default TemplateUsagesModal
